import { useEffect, useState } from "react";
import { useCustodyOrder } from "../../../contexts/CustodyOrderContext";
import { Button } from "../../../design/button";
import { DialogActions, DialogBody, DialogTitle } from "../../../design/dialog";
import { Input } from "../../../design/input";
import {
  Field,
  Fieldset,
  FieldGroup,
  Label,
  ErrorMessage,
} from "../../../design/fieldset";
import { Textarea } from "../../../design/textarea";
import { Select } from "../../../design/select";
import { toLocalDateString } from "../../../utils/timeString";
import { sideTypes, SideType } from "../../../models/SideType";
import { extractFieldErrors } from "../../../utils/formErrorUtils";
import {
  CustodyOrder,
  CustodyOrderType,
  custodyOrderTypes,
} from "../../../models/CustodyOrder";

interface Props {
  onCancel: () => void;
  onCustodyOrderCreated: (id: CustodyOrder["_id"]) => void;
}

interface Errors {
  title?: string;
  description?: string;
  type?: string;
  orderDate?: string;
}

const CustodyOrderFormStep: React.FC<Props> = ({
  onCustodyOrderCreated,
  onCancel,
}) => {
  const { createCustodyOrder } = useCustodyOrder();
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState<CustodyOrderType | null>(null);
  const [orderDate, setOrderDate] = useState<string>(
    toLocalDateString(new Date()).slice(0, 10),
  );
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Errors>({});

  useEffect(() => {
    if (file) {
      setTitle(file.name);
    }
  }, [file]);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!title) {
      newErrors.title = "Title is required.";
    }
    if (!type) {
      newErrors.type = "Type is required.";
    }
    if (!orderDate) {
      newErrors.orderDate = "Order date is required.";
    } else {
      const date = new Date(orderDate);
      if (isNaN(date.getTime())) {
        newErrors.orderDate = "Invalid date format.";
      }
    }
    if (!file) {
      newErrors.file = "File is required.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    setIsSaving(true);
    try {
      const custodyOrderId = await createCustodyOrder(file, {
        title,
        description,
        orderDate: new Date(`${orderDate}T00:00:00Z`),
        type: type!,
      });
      onCustodyOrderCreated(custodyOrderId);
      clearForm();
    } catch (e) {
      const fieldErrors = extractFieldErrors(e);
      setErrors((prev) => ({ ...prev, ...fieldErrors }));
    } finally {
      setIsSaving(false);
    }
  };

  const clearForm = () => {
    setErrors({});
  };

  return (
    <>
      <DialogTitle>Create Custody Order</DialogTitle>
      <DialogBody>
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>File</Label>
              <Input
                type="file"
                onChange={(e) =>
                  setFile(e.target.files ? e.target.files[0] : null)
                }
              />
            </Field>
            <Field>
              <Label>Title</Label>
              <Input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="E.g. Initial Custody Order"
              />
              {errors.title && <ErrorMessage>{errors.title}</ErrorMessage>}
            </Field>
            <Field>
              <Label>Type</Label>
              <Select
                value={type || ""}
                onChange={(e) => setType(e.target.value as CustodyOrderType)}
              >
                <option value="">Select type</option>
                {Object.entries(custodyOrderTypes).map(([key, value]) => (
                  <option key={key} value={key}>
                    {value.label}
                  </option>
                ))}
              </Select>
              {errors.title && <ErrorMessage>{errors.title}</ErrorMessage>}
            </Field>
            <Field>
              <Label>Description</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter description"
                rows={3}
              />
              {errors.description && (
                <ErrorMessage>{errors.description}</ErrorMessage>
              )}
            </Field>
            <Field>
              <Label htmlFor="orderDate">Order Date</Label>
              <Input
                id="orderDate"
                type="date"
                value={orderDate}
                onChange={(e) => setOrderDate(e.target.value)}
                disabled={isSaving}
              />
              {errors.orderDate && (
                <ErrorMessage>{errors.orderDate}</ErrorMessage>
              )}
            </Field>
          </FieldGroup>
        </Fieldset>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={isSaving} onClick={handleSubmit}>
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </>
  );
};

export default CustodyOrderFormStep;
